/**
 * @fileoverview Generic client-side PostHog feature-flag gate (FRE-1229).
 *
 * Extracted from the Apple Music launch gate (WEB-448, `musicConnections.ts`)
 * so every dark-launched surface shares one fail-closed evaluation path
 * instead of re-implementing it per flag.
 *
 * **Fail closed.** A flag reads `false` whenever it cannot be positively
 * confirmed on: PostHog is not initialized (which, per `AnalyticsProvider`,
 * only happens *after* the user grants analytics consent — FRE-809), the
 * client build lacks flag support, or the flag has not resolved to an explicit
 * `true`. A dark launch must never leak the surface.
 */
'use client';

import { useEffect, useState } from 'react';

import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  ANALYTICS_POSTHOG_READY_EVENT,
  getPostHog,
} from '@/lib/analytics';

/**
 * Whether the given PostHog feature flag is enabled for the current visitor.
 *
 * Reads `posthog.isFeatureEnabled(flag)`, coercing anything other than a
 * strict `true` (including `undefined` while flags load, or a missing /
 * consent-gated client) to `false`. Never throws.
 */
export function isFlagEnabled(flag: string): boolean {
  const client = getPostHog();
  if (!client || typeof client.isFeatureEnabled !== 'function') return false;
  try {
    return client.isFeatureEnabled(flag) === true;
  } catch {
    // A flag-eval failure must fail closed, never surface the dark UI.
    return false;
  }
}

/**
 * JSON payload attached to the given PostHog feature flag (FRE-1231), or
 * `undefined` when it cannot be positively read: PostHog missing /
 * consent-gated, the client build lacks payload support, flags not loaded,
 * no payload configured, or a read failure. Never throws.
 *
 * Callers own validating the payload shape — treat the result as untrusted
 * remote input and fail closed on anything malformed.
 */
export function getFlagPayload(flag: string): unknown {
  const client = getPostHog();
  if (!client || typeof client.getFeatureFlagPayload !== 'function') return undefined;
  try {
    return client.getFeatureFlagPayload(flag);
  } catch {
    // A payload-read failure must fail closed, never surface the dark UI.
    return undefined;
  }
}

/**
 * Shared subscription core for the flag hooks below: evaluates `read(flag)`
 * on mount, on every PostHog flag (re)load, when analytics consent flips, and
 * when PostHog registers after a late init (FRE-1269).
 *
 * `read` MUST be a stable (module-level) function — it sits in the effect
 * dependency array, so an inline closure would resubscribe every render.
 */
function useFlagValue<T>(flag: string, read: (flag: string) => T, initial: T): T {
  const [value, setValue] = useState<T>(initial);
  // Bumps when consent flips *or* PostHog becomes ready, forcing the
  // subscription effect to re-run so it can (re)subscribe to a client that
  // initialized after mount (already-granted loads never fire consent-changed).
  const [clientTick, setClientTick] = useState(0);

  useEffect(() => {
    const bumpClientTick = (): void => setClientTick((tick) => tick + 1);
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, bumpClientTick);
    window.addEventListener(ANALYTICS_POSTHOG_READY_EVENT, bumpClientTick);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, bumpClientTick);
      window.removeEventListener(ANALYTICS_POSTHOG_READY_EVENT, bumpClientTick);
    };
  }, []);

  useEffect(() => {
    // Re-check on mount / consent flip / PostHog ready — the client may have
    // registered between render and effect.
    setValue(read(flag));

    const client = getPostHog();
    if (!client || typeof client.onFeatureFlags !== 'function') return;
    // onFeatureFlags fires on every flag (re)load, including the initial one.
    return client.onFeatureFlags(() => setValue(read(flag)));
  }, [flag, read, clientTick]);

  return value;
}

/**
 * React hook wrapping {@link isFlagEnabled} that re-renders when the flag
 * state changes — because PostHog (re)loaded flags, consent flipped, or
 * PostHog registered after a late init (FRE-1269).
 *
 * Starts fail-closed (`false`) and upgrades once the flag resolves to `true`.
 */
export function useFlagEnabled(flag: string): boolean {
  return useFlagValue(flag, isFlagEnabled, false);
}

/**
 * React hook wrapping {@link getFlagPayload} with the same re-render triggers
 * as {@link useFlagEnabled}. Starts fail-closed (`undefined`) and settles once
 * flags load. The payload is untrusted remote JSON — validate before use.
 */
export function useFlagPayload(flag: string): unknown {
  return useFlagValue<unknown>(flag, getFlagPayload, undefined);
}
